import { Component, OnInit } from '@angular/core';
import { DatesRange } from '../common/date-range-picker/date-range-picker.component';
import { FunctionsHelper } from 'src/app/helpers/functions';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ProductCollectionService } from 'src/app/services/product-collection.service';
import { ToastrService } from 'ngx-toastr';
import { LoaderService } from 'src/app/services/loader.service';
import { finalize } from 'rxjs';
import { saveAs } from 'file-saver';
import { NewReportComponent } from './new-report/new-report.component';
import { UpdateReportNameModalComponent } from './update-report-name-modal/update-report-name-modal.component';
import { DeleteReportModalComponent } from './delete-report-modal/delete-report-modal.component';
import { ReportsService } from 'src/app/services/reports.service';
import { ReportItem, ReportStatusLabels } from 'src/app/interfaces/reports.interface';
import { ProductDetailsService } from 'src/app/services/product-details.service';

@Component({
  selector: 'app-reports',
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.scss']
})
export class ReportsComponent implements OnInit {
  refreshingReports: boolean = false;

  page: number = 1;
  pageSize: number = 20;
  collectionSize: number = 0;
  records_from = (): number => (this.page - 1) * this.pageSize + 1;
  records_to = (): number => Math.min((this.page - 1) * this.pageSize + this.pageSize, this.collectionSize);

  reports: ReportItem[] = [];
  filteredReports: ReportItem[] = [];
  sortedReports: ReportItem[] = [];

  sortCol: string = 'createdAt';
  sortOrder: string = 'asc';

  ReportStatusLabels = ReportStatusLabels;

  autoRefreshCount: number = 0;
  maxAutoRefreshCount: number = 5;
  autoRefreshInterval: number = 5000;

  fromDate!: string | undefined;
  toDate!: string | undefined;

  searchStr: string = '';

  constructor(
    private _functionsHelper: FunctionsHelper,
    private modalService: NgbModal,
    private productCollectionService: ProductCollectionService,
    private productDetailsService: ProductDetailsService,
    private reportsService: ReportsService,
    private toastr: ToastrService,
    private loaderService: LoaderService
  ) { }

  ngOnInit(): void {
    // this.openNewReportModal();
    setTimeout(() => {
      this.loaderService.loaderSubject.next(true);
    });
  }

  sortData(column: string, order: string | null = null) {
    if (column != this.sortCol) this.sortOrder = '';
    this.sortCol = column;
    this.sortOrder = order != null ? order : this._functionsHelper.sortRotate(this.sortOrder);

    if (this.sortOrder == '') {
      this.sortedReports = [...this.filteredReports];
    } else {
      this.sortedReports = [...this.filteredReports].sort((a: any, b: any) => {
        const res = this._functionsHelper.sortCompare(a[column], b[column]);
        return this.sortOrder === 'desc' ? res : -res;
      });
    }
    // console.log('this.sortedReports', this.sortedReports);
  }

  refreshReports(callback?: () => void) {
    this.refreshingReports = true;
    this.reportsService.getReports({
      fromDate: this.fromDate,
      toDate: this.toDate,
    })
      .finally(() => {
        this.refreshingReports = false;
        this.loaderService.loaderSubject.next(false);
      })
      .then((response) => {
        this.reports = response;
        this.filteredReports = [...this.reports];
        this.collectionSize = this.reports.length;
        this.sortData(this.sortCol, this.sortOrder);
        if (callback) callback();
      })
      .catch(() => {
        this.toastr.error('Something went wrong while fetching reports.');
      });
  }

  autoRefreshReports() {
    this.refreshReports(() => {
      this.autoRefreshCount++;
      if (this.autoRefreshCount < this.maxAutoRefreshCount) {
        setTimeout(() => {
          this.autoRefreshReports();
        }, this.autoRefreshInterval);
      } else {
        this.autoRefreshCount = 0;
      }
    });
  }

  openNewReportModal() {
    this.reportsService.selectedFeature = null;
    this.productCollectionService.categoryIds = '';
    this.productDetailsService.fileColumns = [];
    this.productDetailsService.fileRows = [];
    this.productDetailsService.newAnalysisFile = null;
    this.productDetailsService.productIdColumnIndex = null;
    this.productDetailsService.selectedIdentifier = null;
    this.productDetailsService.identifierColumnIndex = null;

    this.modalService.open(NewReportComponent, {
      centered: true,
      modalDialogClass: 'modal-cls1',
      size: 'lg',
      scrollable: true,
      backdrop: 'static',
      keyboard: false
    }).result.then(
      () => {
        this.autoRefreshReports();
      },
      () => {
        this.reportsService.selectedFeature = null;
      }
    );
  }

  downloadReport(report: ReportItem) {
    report.downloading = true;
    this.reportsService.getReportUrl(report.reportId)
      .then((reportUrl) => {
        window.open(reportUrl, '_blank');
        report.downloading = false;
        // report.downloadSubscription = this.reportsService.downloadReport(reportUrl)
        //   .pipe(finalize(() => {
        //     report.downloading = false;
        //   }))
        //   .subscribe({
        //     next: (response) => {
        //       if (response.byteLength > 0) {
        //         const blob = new Blob([response], { type: 'application/octet-stream' });
        //         saveAs(blob, report.reportId + '.zip');
        //       }
        //     },
        //     error: () => {
        //       this.toastr.error('Something went wrong downloading report.');
        //     }
        //   });
      })
      .catch(() => {
        report.downloading = false;
        this.toastr.error('Something went wrong while linking count to report.');
      });
  }

  cancelReportDownload(report: ReportItem): void {
    if (report.downloadSubscription) {
      report.downloadSubscription.unsubscribe();
      report.downloading = false;
    }
  }

  generateReportFromCount(report: ReportItem) {
    report.generating = true;
    this.productCollectionService.linkCountToReport(report.reportId)
      .finally(() => {
        report.generating = false;
      })
      .then(() => {
        report.status = 'QUEUED';
      })
      .catch(() => {
        this.toastr.error('Something went wrong while linking count to report.');
      });
  }

  loadReportsFilteredByDates = (dates: DatesRange) => {
    this.fromDate = this._functionsHelper.ngbDateToMoment(dates.fromDate)?.format('YYYY-MM-DD');
    this.toDate = this._functionsHelper.ngbDateToMoment(dates.toDate)?.format('YYYY-MM-DD');
    this.refreshReports();
  };

  onSearchReports() {
    this.filteredReports = this.reports.filter(report => {
      return report.reportName.toLowerCase().includes(this.searchStr.toLowerCase());
    });
    this.sortData(this.sortCol, this.sortOrder);
  }

  deleteReport(report: ReportItem) {
    const modalRef = this.modalService.open(DeleteReportModalComponent, {
      centered: true,
      modalDialogClass: 'modal-cls1',
    });
    modalRef.componentInstance.report = report;
    modalRef.result.then(
      (result) => {
        if (result === 'deleted') {
          this.sortedReports = this.sortedReports.filter((r) => r.reportId !== report.reportId);
        }
      }
    );
  }

  openEditReportNameModal(report: ReportItem) {
    const modalRef = this.modalService.open(UpdateReportNameModalComponent, {
      centered: true,
      modalDialogClass: 'modal-cls1',
    });
    modalRef.componentInstance.report = report;
  }

}
